import type { HTMLAttributes } from 'react';

import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/utils';

import type { ObservationProps } from './observation';

import { Observation } from './observation';

export type ModuleProps = HTMLAttributes<HTMLDivElement> & {
    observations: ObservationProps[];
    isPreview: boolean;
    objectId: string;
    moduleType: string;
    isDisabled?: boolean;
};

export function Module({
    observations,
    isPreview,
    className,
    moduleType,
    objectId,
    children,
    isDisabled,
    ...props
}: ModuleProps) {
    const content = (
        <div
            className={cn(
                'border p-2 flex w-full flex-col rounded-2xl relative',
                isPreview ? 'bg-background-secondary' : '',
                isDisabled ? 'pointer-events-none opacity-60' : '',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );

    const routesList = [
        {
            route: 'electrical',
            path: `/customer/locations/$locationId/${moduleType.toLowerCase()}`,
        },
    ];

    return isDisabled
        ? content
        : (
                <Link
                    to={isPreview && moduleType ? `/customer/locations/$locationId/preview/$moduleType` : `${routesList.find(x => x.route === moduleType)?.path}`}
                    params={{ locationId: objectId, moduleType }}
                >
                    {content}
                </Link>
            );
}

Module.Icon = function ModuleIcon({
    className,
    children,
    ...props
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cn('flex items-center gap-1', className)} {...props}>
            {children}
        </div>
    );
};

Module.Title = function ModuleTitle({
    className,
    children,
    ...props
}: HTMLAttributes<HTMLHeadingElement>) {
    return (
        <h2 className={cn('text-md font-bold', className)} {...props}>
            {children}
        </h2>
    );
};

Module.Observations = function ModuleObservations({
    observations,
    className,
    ...props
}: { observations: ObservationProps[] } & HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cn('flex w-full gap-1', className)} {...props}>
            {observations.map(observation => (
                <div key={observation.label} className="m-0.5">
                    <Observation {...observation} />
                </div>
            ))}
        </div>
    );
};
